import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SubmitButton } from '../Button';
import Form from './Form';
import Label from './Label';
import FormError from './FormError';
import FormErrorInline from './FormErrorInline';
import { EmailInput, PasswordCriteria, PasswordInput } from '../Input';
import Icon from '../Icon';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import {
  AUTH_ACTIONS,
  AUTH_FLOWS,
  AUTH_METHODS,
  AUTH_STEPS,
  buildAuthDdActionName,
  logAuthApiError,
} from '../../utils/authDatadog';

import Check from '../../svg/icons/check.svg';
import { postForgotPassword, postUserResetPassword } from '../../api/api';

const INVALID = 403;
const INACTIVE = 409;

export function getErrorMsg(status) {
  if (status === INVALID) {
    return (
      <TranslationProvider translationKey="errors.parse.invalid">
        <span>It looks like this password reset attempt has expired. Please try again.</span>
      </TranslationProvider>
    );
  }

  if (status === INACTIVE) {
    return (
      <TranslationProvider translationKey="errors.parse.inactive">
        <span>Your account is no longer active. Please sign up for a new account</span>
      </TranslationProvider>
    );
  }

  return (
    <TranslationProvider translationKey="errors.parse.try_again">
      <span>Error. Please try again.</span>
    </TranslationProvider>
  );
}
const getModeFromRouteParams = (params = {}) => (params.key ? 'set' : 'reset');
const getSuccess = (data) => {
  if (data.password) {
    return (
      <TranslationProvider translationKey="set_password_success">
        <span>Success. Your new password has been set.</span>
      </TranslationProvider>
    );
  }
  return (
    <TranslationProvider translationKey="reset_password_success">
      {'If a Resy account has been set up using this email address, you\'ll receive an email shortly.'}
    </TranslationProvider>
  );
};

const PasswordReset = ({
  Analytics,
  $routeParams,
}) => {
  const [passwordCriteriaOpen, setPasswordCriteriaOpen] = useState(false);
  const mode = getModeFromRouteParams($routeParams);

  const onBlur = (e) => {
    setPasswordCriteriaOpen(!!e.currentTarget.value.length);
  };

  const onFocus = () => {
    setPasswordCriteriaOpen(true);
  };

  const sendPassword = (data) => {
    const fields = {};
    let error = false;

    if (data.password_confirm.trim() === '') {
      error = true;
      document.querySelector('#password_confirm').focus();
      fields.password_confirm = '';
    }

    if (data.password.trim() === '') {
      error = true;
      document.querySelector('#password').focus();
      fields.password = getTranslation({
        fallback: 'Password must contain at least 8 characters with at least 1 letter, 1 number, and 1 symbol (!?$^& etc.).',
        translationKey: 'errors.password',
      });
    }

    if (error) {
      return Promise.reject({ // eslint-disable-line prefer-promise-reject-errors
        data: {
          data: fields,
        },
      });
    }
    return postUserResetPassword({ key: $routeParams.key, password: data.password }).catch((apiError) => {
      logAuthApiError({
        flow: AUTH_FLOWS.RESET_PASSWORD,
        method: AUTH_METHODS.EMAIL_PASS,
        step: AUTH_STEPS.SET_NEW_PASSWORD,
        endpoint: '2/user/reset_password',
        error: apiError,
      });
      throw apiError;
    });
  };

  const sendEmail = (data) => {
    const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!EMAIL_REGEX.test(data.email)) {
      document.querySelector('#email').focus();
      return Promise.reject({ // eslint-disable-line prefer-promise-reject-errors
        data: {
          data: {
            email: getTranslation({
              fallback: 'Please enter a valid email address',
              translationKey: 'errors.email',
            }),
          },
        },
      });
    }

    Analytics.event('reset password');
    return postForgotPassword({ email: data.email }).catch((apiError) => {
      logAuthApiError({
        flow: AUTH_FLOWS.RESET_PASSWORD,
        method: AUTH_METHODS.EMAIL_PASS,
        step: AUTH_STEPS.REQUEST_RESET_EMAIL,
        endpoint: '2/forgot_password',
        error: apiError,
      });
      throw apiError;
    });
  };

  const onSubmit = (mod) => (mod === 'set' ? sendPassword : sendEmail);

  return (
    <div className="PasswordResetForm__container">
      <div className="PasswordResetForm">
        <TranslationProvider translationKey="titles.reset_password">
          <h1>
            Reset Password
          </h1>
        </TranslationProvider>

        {mode === 'reset'
          && (
            <TranslationProvider translationKey="password_reset_copy">
              <p className="PasswordResetForm__copy">You know the drill. Please start by providing the email address you used to register.</p>
            </TranslationProvider>
          )}

        {mode === 'set'
          && (
            <TranslationProvider translationKey="password_set_copy">
              <p className="PasswordResetForm__copy">Please set a new password and then type it again. That way you won’t forget it (again).</p>
            </TranslationProvider>
          )}

        <Form
          name="reset_password_form"
          onSubmit={onSubmit(mode)}
          noValidate
          datadogActionName={buildAuthDdActionName({
            flow: AUTH_FLOWS.RESET_PASSWORD,
            method: AUTH_METHODS.EMAIL_PASS,
            step: mode === 'set' ? AUTH_STEPS.SET_NEW_PASSWORD : AUTH_STEPS.REQUEST_RESET_EMAIL,
            action: AUTH_ACTIONS.SUBMIT,
          })}
        >
          {({
            errorData, errorKeys, errorStatus, msg, onChange, processing, success, values,
          }) => (
            <>
              {msg
                && <span>{msg}</span>}
              {mode === 'reset'
                && (
                  <>
                    <TranslationProvider
                      translationKey="email_address"
                    >
                      <Label forInput="email" required>
                        Email
                      </Label>
                    </TranslationProvider>
                    <EmailInput
                      id="email"
                      onChange={onChange}
                      errors={errorKeys}
                      ariaRequired
                      ariaDescribedby="error_email"
                    />
                    {errorData
                      && errorData.email
                      && <FormErrorInline id="error_email">{errorData.email}</FormErrorInline>}
                  </>
                )}
              {mode === 'set'
                && (
                  <>
                    <TranslationProvider
                      translationKey="password"
                    >
                      <Label forInput="password" required>
                        Password
                      </Label>
                    </TranslationProvider>
                    <PasswordInput
                      type="new"
                      id="password"
                      onBlur={onBlur}
                      onChange={onChange}
                      onFocus={onFocus}
                      errors={errorKeys}
                    />
                    {errorData
                      && errorData.password
                      && <FormErrorInline>{errorData.password}</FormErrorInline>}
                    <TranslationProvider
                      translationKey="password_confirm"
                    >
                      <Label forInput="password_confirm" required>
                        Confirm Password
                      </Label>
                    </TranslationProvider>
                    <PasswordInput
                      id="password_confirm"
                      errors={errorKeys}
                      name="password_confirm"
                      onChange={onChange}
                      placeholder="Confirm Password"
                      type="new"
                    />
                    {errorData
                      && errorData.password_confirm
                      && <FormErrorInline>{errorData.password_confirm}</FormErrorInline>}
                    <PasswordCriteria password={values.password} passwordConfirm={values.password_confirm} open={passwordCriteriaOpen} />
                  </>
                )}
              <TranslationProvider
                translationKey="address_form.submit"
              >
                <SubmitButton
                  lg
                  processing={processing}
                  disabled={values.password !== values.password_confirm}
                  data-dd-action-name={buildAuthDdActionName({
                    flow: AUTH_FLOWS.RESET_PASSWORD,
                    method: AUTH_METHODS.EMAIL_PASS,
                    step: mode === 'set' ? AUTH_STEPS.SET_NEW_PASSWORD : AUTH_STEPS.REQUEST_RESET_EMAIL,
                    action: AUTH_ACTIONS.SUBMIT,
                  })}
                >
                  Submit
                </SubmitButton>
              </TranslationProvider>
              {success
                && (
                  <div className="ResyResetPassword__success" role="alert" aria-live="polite">
                    <span><Icon component={Check} name="check" /></span>
                    <span>
                      {getSuccess(values)}
                    </span>
                  </div>
                )}
              {
                errorStatus
                && <FormError>{getErrorMsg(errorStatus)}</FormError>
              }
            </>
          )}
        </Form>
      </div>
    </div>
  );
};

const passwordResetPropTypes = {
  $routeParams: PropTypes.shape({
    key: PropTypes.string,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
};

PasswordReset.propTypes = passwordResetPropTypes;

export { passwordResetPropTypes };
export default PasswordReset;
